import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Plane, Train, Bus, Hotel, MapPin, Video, Image, BookOpen, Phone, User, LogOut, ChevronDown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const navLinks = [
  { path: '/packages', label: 'Tour Packages', icon: MapPin },
  { path: '/flights', label: 'Flights', icon: Plane },
  { path: '/trains', label: 'Trains', icon: Train },
  { path: '/buses', label: 'Buses', icon: Bus },
  { path: '/hotels', label: 'Hotels', icon: Hotel },
  { path: '/trip-planner', label: 'Plan Trip', icon: MapPin },
  { path: '/videos', label: 'Videos', icon: Video },
  { path: '/gallery', label: 'Gallery', icon: Image },
  { path: '/blog', label: 'Blog', icon: BookOpen },
  { path: '/contact', label: 'Contact', icon: Phone },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const isHome = location.pathname === '/';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || !isHome
          ? 'bg-white/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
      data-testid="navbar"
    >
      <nav className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center">
              <span className="text-white font-bold text-lg">ID</span>
            </div>
            <span className={`font-bold text-xl ${scrolled || !isHome ? 'text-slate-900' : 'text-white'}`}>
              ID4U Travels
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.slice(0, 5).map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.path.slice(1)}`}
                className={`px-3 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  location.pathname === link.path
                    ? 'bg-teal-100 text-teal-700'
                    : scrolled || !isHome
                    ? 'text-slate-700 hover:bg-slate-100'
                    : 'text-white/90 hover:bg-white/10'
                }`}
              >
                {link.label}
              </Link>
            ))}
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className={`px-3 py-2 rounded-full text-sm font-medium transition-all duration-300 flex items-center gap-1 ${
                    scrolled || !isHome
                      ? 'text-slate-700 hover:bg-slate-100'
                      : 'text-white/90 hover:bg-white/10'
                  }`}
                  data-testid="more-menu-trigger"
                >
                  More <ChevronDown className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {navLinks.slice(5).map((link) => (
                  <DropdownMenuItem key={link.path} asChild>
                    <Link to={link.path} className="flex items-center gap-2" data-testid={`nav-${link.path.slice(1)}`}>
                      <link.icon className="w-4 h-4" />
                      {link.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Auth Buttons */}
          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className={`flex items-center gap-2 px-4 py-2 rounded-full font-medium transition-all ${
                      scrolled || !isHome
                        ? 'bg-teal-700 text-white hover:bg-teal-800'
                        : 'bg-white text-teal-700 hover:bg-white/90'
                    }`}
                    data-testid="user-menu-trigger"
                  >
                    <User className="w-4 h-4" />
                    {user.name?.split(' ')[0]}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={logout} data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link
                to="/auth"
                data-testid="login-btn"
                className={`px-6 py-2 rounded-full font-medium transition-all duration-300 ${
                  scrolled || !isHome
                    ? 'bg-teal-700 text-white hover:bg-teal-800'
                    : 'bg-white text-teal-700 hover:bg-white/90'
                }`}
              >
                Login
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 rounded-lg"
            data-testid="mobile-menu-btn"
          >
            {isOpen ? (
              <X className={scrolled || !isHome ? 'text-slate-900' : 'text-white'} />
            ) : (
              <Menu className={scrolled || !isHome ? 'text-slate-900' : 'text-white'} />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-white rounded-xl shadow-xl mb-4 overflow-hidden"
              data-testid="mobile-menu"
            >
              <div className="p-4 space-y-1">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    data-testid={`mobile-nav-${link.path.slice(1)}`}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                      location.pathname === link.path
                        ? 'bg-teal-100 text-teal-700'
                        : 'text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    <link.icon className="w-5 h-5" />
                    {link.label}
                  </Link>
                ))}
                <div className="pt-4 border-t mt-4">
                  {user ? (
                    <button
                      onClick={logout}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-slate-100 text-slate-700 rounded-xl"
                      data-testid="mobile-logout-btn"
                    >
                      <LogOut className="w-5 h-5" />
                      Logout
                    </button>
                  ) : (
                    <Link
                      to="/auth"
                      className="block w-full text-center px-4 py-3 bg-teal-700 text-white rounded-xl font-medium"
                      data-testid="mobile-login-btn"
                    >
                      Login / Sign Up
                    </Link>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
}
